/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
/* eslint-disable theme-colors/no-literal-colors */
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import type {
  Editor as OrigEditor,
  IEditSession,
  Position,
  TextMode as OrigTextMode,
} from 'brace';
// import AceEditor, { IAceEditorProps } from 'react-ace';
// import { config } from 'ace-builds';
// import { acequire } from 'ace-builds/src-noconflict/ace';
import type AceEditor from 'react-ace';
import type { IAceEditorProps } from 'react-ace';

import AsyncEsmComponent, {
  PlaceholderProps,
} from 'src/components/AsyncEsmComponent';
import useEffectEvent from 'src/hooks/useEffectEvent';
import { AceEditorFrame } from './editorStyles';
// import cssWorkerUrl from 'ace-builds/src-noconflict/worker-css';

// config.setModuleUrl('ace/mode/css_worker', cssWorkerUrl);

export interface AceCompleterKeywordData {
  name: string;
  value: string;
  score: number;
  meta: string;
  docText?: string;
  docHTML?: string;
}

export type TextMode = OrigTextMode & { $id: string };

export interface AceCompleter {
  insertMatch: (
    data?: Editor | { value: string } | string,
    options?: AceCompleterKeywordData,
  ) => void;
}

export type Editor = OrigEditor & {
  completer: AceCompleter;
  completers: AceCompleter[];
};

export interface AceCompleterKeyword extends AceCompleterKeywordData {
  completer?: AceCompleter;
}

/**
 * Async loaders to import brace modules. Must manually create call `import(...)`
 * promises because webpack can only analyze async imports statically.
 */
const aceModuleLoaders = {
  'mode/sql': () => import('brace/mode/sql'),
  'mode/markdown': () => import('brace/mode/markdown'),
  'mode/css': () => import('brace/mode/css'),
  'mode/json': () => import('brace/mode/json'),
  'mode/yaml': () => import('brace/mode/yaml'),
  'mode/html': () => import('brace/mode/html'),
  'mode/javascript': () => import('brace/mode/javascript'),
  // @ts-ignore - brace 没有 python mode 的类型声明
  'mode/python': () => import('brace/mode/python'),
  'theme/textmate': () => import('brace/theme/textmate'),
  'theme/github': () => import('brace/theme/github'),
  // @ts-ignore - brace 主题没有类型声明
  'theme/tomorrow_night': () => import('brace/theme/tomorrow_night'),
  'ext/language_tools': () => import('brace/ext/language_tools'),
  'ext/searchbox': () => import('brace/ext/searchbox'),
  'ext/beautify': () => import('brace/ext/beautify'),
};

export type AceModule = keyof typeof aceModuleLoaders;

export type AsyncAceEditorProps = IAceEditorProps & {
  keywords?: AceCompleterKeyword[];
};

export type AceEditorMode = 'sql';
export type AceEditorTheme = 'textmate' | 'github' | 'tomorrow_night';
export type AsyncAceEditorOptions = {
  defaultMode?: AceEditorMode;
  defaultTheme?: AceEditorTheme;
  defaultTabSize?: number;
  fontFamily?: string;
  placeholder?: React.ComponentType<
    PlaceholderProps & Partial<IAceEditorProps>
  > | null;
};

/**
 * Get an async AceEditor with automatical loading of specified ace modules.
 */
export default function AsyncAceEditor(
  aceModules: AceModule[],
  {
    defaultMode,
    defaultTheme,
    defaultTabSize = 2,
    fontFamily = 'Menlo, Consolas, Courier New, Ubuntu Mono, source-code-pro, Lucida Console, monospace',
    placeholder,
  }: AsyncAceEditorOptions = {},
) {
  return AsyncEsmComponent(async () => {
    // const { default: ReactAceEditor } = await import('react-ace');
    const reactAcePromise = import('react-ace');
    const aceBuildsConfigPromise = import('ace-builds');
    const acequirePromise = import('ace-builds/src-min-noconflict/ace');

    // 按需加载 worker，避免不必要的资源加载
    const needsCssWorker = aceModules.includes('mode/css');
    const needsJsWorker = aceModules.includes('mode/javascript');
    const workerPromises: Promise<{ default: string }>[] = [];
    if (needsCssWorker) {
      workerPromises.push(
        import('ace-builds/src-min-noconflict/worker-css') as Promise<{
          default: string;
        }>,
      );
    }
    if (needsJsWorker) {
      workerPromises.push(
        import('ace-builds/src-min-noconflict/worker-javascript') as Promise<{
          default: string;
        }>,
      );
    }

    const [{ default: ReactAceEditor }, { config }, { acequire }, ...workers] =
      await Promise.all([
        reactAcePromise,
        aceBuildsConfigPromise,
        acequirePromise,
        ...workerPromises,
      ]);

    // 配置 worker URL
    let workerIndex = 0;
    if (needsCssWorker) {
      config.setModuleUrl('ace/mode/css_worker', workers[workerIndex].default);
      workerIndex += 1;
    }
    if (needsJsWorker) {
      config.setModuleUrl(
        'ace/mode/javascript_worker',
        workers[workerIndex].default,
      );
    }

    await Promise.all(aceModules.map(x => aceModuleLoaders[x]()));

    const inferredMode =
      defaultMode ||
      aceModules.find(x => x.startsWith('mode/'))?.replace('mode/', '');
    const inferredTheme =
      defaultTheme ||
      aceModules.find(x => x.startsWith('theme/'))?.replace('theme/', '');

    const resizeAceInstance = (instance: AceEditor | null) => {
      const editor = instance?.editor as Editor | undefined;
      if (!editor) return;
      editor.resize();
      editor.renderer.updateFull(true);
    };

    return forwardRef<AceEditor, AsyncAceEditorProps>(
      function ExtendedAceEditor(
        {
          keywords,
          mode = inferredMode,
          theme = inferredTheme,
          tabSize = defaultTabSize,
          defaultValue = '',
          style,
          onLoad,
          height: heightProp,
          minLines,
          maxLines,
          ...props
        },
        ref,
      ) {
        const containerRef = useRef<HTMLDivElement>(null);
        const aceRef = useRef<AceEditor | null>(null);
        const langTools = acequire('ace/ext/language_tools');

        const setAceRef = useCallback(
          (instance: AceEditor | null) => {
            aceRef.current = instance;
            if (typeof ref === 'function') {
              ref(instance);
            } else if (ref) {
              // eslint-disable-next-line no-param-reassign
              (ref as React.MutableRefObject<AceEditor | null>).current =
                instance;
            }
          },
          [ref],
        );

        const scheduleResize = useEffectEvent(() => {
          requestAnimationFrame(() => resizeAceInstance(aceRef.current));
        });

        const setCompleters = useEffectEvent(
          (nextKeywords: AceCompleterKeyword[]) => {
            const completer = {
              getCompletions: (
                editor: AceEditor,
                session: IEditSession,
                pos: Position,
                prefix: string,
                callback: (error: null, wordList: object[]) => void,
              ) => {
                // If the prefix starts with a number, don't try to autocomplete
                if (!Number.isNaN(parseInt(prefix, 10))) {
                  return;
                }
                if (
                  (session.getMode() as TextMode).$id === `ace/mode/${mode}`
                ) {
                  callback(null, nextKeywords);
                }
              },
            };
            langTools.setCompleters([completer]);
          },
        );

        useEffect(() => {
          if (keywords) {
            setCompleters(keywords);
          }
        }, [keywords, setCompleters]);

        useEffect(() => {
          scheduleResize();
        }, [mode, theme, scheduleResize]);

        useEffect(() => {
          const container = containerRef.current;
          if (!container || typeof ResizeObserver === 'undefined') {
            return undefined;
          }
          const observer = new ResizeObserver(() => {
            scheduleResize();
          });
          observer.observe(container);
          return () => observer.disconnect();
        }, [scheduleResize]);

        const handleLoad: IAceEditorProps['onLoad'] = editor => {
          scheduleResize();
          onLoad?.(editor);
        };

        const normalizedHeight =
          heightProp != null
            ? typeof heightProp === 'number'
              ? `${heightProp}px`
              : String(heightProp)
            : undefined;

        const styleObject =
          typeof style === 'object' && style !== null ? style : undefined;

        const fillParent =
          styleObject?.height === '100%' ||
          styleObject?.height === '100vh' ||
          styleObject?.flex === 1;

        const containerStyle: React.CSSProperties = {
          width: '100%',
        };

        if (normalizedHeight) {
          containerStyle.height = normalizedHeight;
          containerStyle.minHeight = normalizedHeight;
        } else if (fillParent) {
          containerStyle.height = '100%';
          containerStyle.minHeight = 0;
        } else if (minLines != null) {
          containerStyle.minHeight = `${
            Math.max(Number(minLines), 3) * 1.45
          }em`;
        } else if (styleObject?.minHeight) {
          containerStyle.minHeight = styleObject.minHeight;
        } else {
          // JSON/配置类编辑器在模态框内无父级高度时，避免塌成一条线
          containerStyle.minHeight = '160px';
        }

        if (styleObject) {
          Object.assign(containerStyle, styleObject);
        }

        const hasBorderedShell = Boolean(styleObject?.border);
        if (hasBorderedShell) {
          containerStyle.boxSizing = containerStyle.boxSizing ?? 'border-box';
          containerStyle.overflow = containerStyle.overflow ?? 'hidden';
          containerStyle.display = containerStyle.display ?? 'flex';
          containerStyle.flexDirection =
            containerStyle.flexDirection ?? 'column';
        }

        // Ace 使用 percentage height 时，容器必须有明确高度，否则会塌成 0
        if (!containerStyle.height && containerStyle.minHeight && !fillParent) {
          containerStyle.height = containerStyle.minHeight;
        }

        const aceEditorHeight =
          normalizedHeight || fillParent || containerStyle.height
            ? '100%'
            : undefined;

        return (
          <div
            ref={containerRef}
            style={containerStyle}
            {...(hasBorderedShell ? { 'data-ace-frame': '' } : {})}
          >
            <ReactAceEditor
              ref={setAceRef}
              mode={mode}
              theme={theme}
              tabSize={tabSize}
              defaultValue={defaultValue}
              showPrintMargin={false}
              setOptions={{ fontFamily }}
              style={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                flex: hasBorderedShell ? 1 : undefined,
              }}
              onLoad={handleLoad}
              minLines={minLines}
              maxLines={maxLines}
              height={aceEditorHeight}
              {...props}
            />
          </div>
        );
      },
    );
  }, placeholder);
}

export const SQLEditor = AsyncAceEditor([
  'mode/sql',
  'theme/tomorrow_night',
  'ext/language_tools',
  'ext/searchbox',
]);

export const FullSQLEditor = AsyncAceEditor(
  ['mode/sql', 'theme/tomorrow_night', 'ext/language_tools', 'ext/searchbox'],
  {
    // a custom placeholder in SQL lab for less jumpy re-renders
    placeholder: () => {
      const gutterBackground = 'rgb(34, 34, 34)';
      return (
        <div
          style={{
            height: '100%',
          }}
        >
          <div
            style={{ width: 41, height: '100%', background: gutterBackground }}
          />
          {/* make it possible to resize the placeholder */}
          <div className="ace_content" />
        </div>
      );
    },
  },
);

export const MarkdownEditor = AsyncAceEditor(['mode/markdown', 'theme/github']);

export const TextAreaEditor = AsyncAceEditor([
  'mode/markdown',
  'mode/sql',
  'mode/json',
  'mode/html',
  'mode/javascript',
  'mode/python',
  'theme/github',
  'ext/language_tools',
  'ext/searchbox',
  'ext/beautify',
]);

export const CssEditor = AsyncAceEditor([
  'mode/css',
  'theme/github',
  'ext/language_tools',
]);

export const JsonEditor = AsyncAceEditor(['mode/json', 'theme/github']);

/**
 * JSON or Yaml config editor.
 */
export const ConfigEditor = AsyncAceEditor([
  'mode/json',
  'mode/yaml',
  'theme/github',
]);

export {
  AceEditorFrame,
  ModalAceEditorShell,
  EditorGlobalStyles,
} from './editorStyles';

type FrameProps = {
  frameHeight?: number | string;
  frameMinHeight?: number | string;
  frameFlex?: boolean;
  frameClassName?: string;
};

type FramedSQLEditorProps = AsyncAceEditorProps & FrameProps;

type FramedConfigEditorProps = AsyncAceEditorProps & FrameProps;

function renderFramedEditor(
  EditorComponent: typeof SQLEditor,
  {
    frameHeight,
    frameMinHeight,
    frameFlex,
    frameClassName,
    height,
    width,
    style,
    editorProps,
    ...editorPropsRest
  }: AsyncAceEditorProps & FrameProps,
  ref: React.Ref<AceEditor>,
) {
  return (
    <AceEditorFrame
      className={frameClassName}
      $height={frameHeight}
      $minHeight={frameMinHeight}
      $flex={frameFlex}
    >
      <EditorComponent
        ref={ref as React.Ref<any>}
        height={height ?? '100%'}
        width={width ?? '100%'}
        style={{ width: '100%', height: '100%', ...style }}
        editorProps={{ $blockScrolling: true, ...editorProps }}
        {...editorPropsRest}
      />
    </AceEditorFrame>
  );
}

/** 标准 SQL 编辑器：AceEditorFrame + SQLEditor */
export const FramedSQLEditor = forwardRef<AceEditor, FramedSQLEditorProps>(
  function FramedSQLEditor(props, ref) {
    return renderFramedEditor(SQLEditor, props, ref);
  },
);

/** 标准 JSON/YAML 配置编辑器：AceEditorFrame + ConfigEditor */
export const FramedConfigEditor = forwardRef<
  AceEditor,
  FramedConfigEditorProps
>(function FramedConfigEditor(props, ref) {
  return renderFramedEditor(ConfigEditor, props, ref);
});
